import React, { useEffect, useRef, useState } from "react";
import { X, Plus, Trash2, Loader2, Upload, PenLine, Eraser, Building2, User } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const BANK = [["bank_name", "Banque"], ["bank_address", "Adresse"], ["bank_city", "Ville"], ["bank_postal_code", "Code postal"], ["bank_country", "Pays"], ["iban", "IBAN"], ["bic", "Swift/BIC"], ["account_number", "Numéro de compte"], ["bank_code", "Code banque"], ["branch_code", "Code guichet"], ["rib_key", "Clé RIB"]];
const REQUIRED = {
  "Société": [["company", "Société"], ["email", "E-mail"], ["mobile", "Mobile"], ["city", "Ville"], ["postal_code", "Code postal"], ["country", "Pays"]],
  Particulier: [["civilite", "Civilité"], ["prenom", "Prénom"], ["nom", "Nom"], ["email", "E-mail"], ["mobile", "Mobile"], ["city", "Ville"], ["postal_code", "Code postal"], ["country", "Pays"]],
};
const EMPTY = { owner_type: "Particulier", country: "France" };
const fileUrl = (p) => (p ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}` : "");

function Section({ title, hint, children }) {
  return (
    <div className="space-y-3">
      <div><p className="text-sm font-semibold text-brand-greenDeep">{title}</p>{hint && <p className="text-xs text-brand-green/50">{hint}</p>}</div>
      {children}
    </div>
  );
}

function SignaturePad({ onSave }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const [dirty, setDirty] = useState(false);
  const pos = (e) => { const r = ref.current.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
  const down = (e) => { drawing.current = true; const c = ref.current.getContext("2d"); c.lineWidth = 2; c.lineCap = "round"; c.strokeStyle = "#121311"; c.beginPath(); c.moveTo(...pos(e)); ref.current.setPointerCapture(e.pointerId); };
  const move = (e) => { if (!drawing.current) return; const c = ref.current.getContext("2d"); c.lineTo(...pos(e)); c.stroke(); setDirty(true); };
  const clear = () => { ref.current.getContext("2d").clearRect(0, 0, ref.current.width, ref.current.height); setDirty(false); };
  const save = () => ref.current.toBlob((b) => onSave(b), "image/png");
  return (
    <div className="space-y-2">
      <canvas ref={ref} width={420} height={140} className="w-full max-w-[420px] h-[140px] rounded-xl border border-dashed border-brand-soft bg-white touch-none cursor-crosshair"
        onPointerDown={down} onPointerMove={move} onPointerUp={() => { drawing.current = false; }} data-testid="owner-signature-canvas" />
      <div className="flex gap-2">
        <button type="button" onClick={clear} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-brand-soft text-brand-green/70" data-testid="owner-signature-clear"><Eraser className="w-3.5 h-3.5" /> Effacer</button>
        <button type="button" onClick={save} disabled={!dirty} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-brand-greenDeep text-white disabled:opacity-50" data-testid="owner-signature-use"><PenLine className="w-3.5 h-3.5" /> Utiliser ce dessin</button>
      </div>
    </div>
  );
}

function AssetField({ kind, path, onChange, label, hint }) {
  const [busy, setBusy] = useState(false);
  const [draw, setDraw] = useState(false);
  const upload = async (blob, name) => {
    if (blob.size > 15 * 1024 * 1024) { toast.error("Taille maximale : 15 Mo"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", blob, name); fd.append("kind", kind);
      const { data } = await api.post("/owners/upload-asset", fd);
      onChange(data.path); setDraw(false); toast.success(kind === "logo" ? "Logo ajouté" : "Signature ajoutée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible"); }
    finally { setBusy(false); }
  };
  return (
    <Section title={label} hint={hint}>
      <div className="flex flex-wrap items-center gap-3">
        {path && <img src={fileUrl(path)} alt={label} className="h-14 max-w-[160px] object-contain rounded-lg border border-brand-soft bg-white p-1" data-testid={`owner-${kind}-preview`} />}
        <label className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full border border-brand-greenDeep text-brand-greenDeep font-semibold cursor-pointer hover:bg-brand-softer">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Parcourir
          <input type="file" accept=".gif,.jpg,.jpeg,.png,image/gif,image/jpeg,image/png" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], e.target.files[0].name)} data-testid={`owner-${kind}-file`} />
        </label>
        {kind === "signature" && <button type="button" onClick={() => setDraw((d) => !d)} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full border border-brand-soft text-brand-greenDeep" data-testid="owner-signature-draw"><PenLine className="w-3.5 h-3.5" /> Dessiner ma signature</button>}
        {path && <button type="button" onClick={() => onChange("")} className="text-xs text-red-500" data-testid={`owner-${kind}-remove`}>Retirer</button>}
      </div>
      <p className="text-[11px] text-brand-green/45">Formats acceptés : GIF, JPG, PNG. Taille maximale : 15 Mo.</p>
      {draw && <><p className="text-[11px] text-brand-green/50">Dessinez votre signature à la main avec la souris, le trackpad ou le doigt.</p><SignaturePad onSave={(b) => upload(b, "signature.png")} /></>}
    </Section>
  );
}

function OwnerForm({ initial, onSaved, onCancel }) {
  const [f, setF] = useState({ ...EMPTY, ...initial, owner_type: (initial.owner_type || "Particulier").startsWith("Soci") ? "Société" : "Particulier" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k, v) => { setF((x) => ({ ...x, [k]: v })); setErrors((e) => ({ ...e, [k]: false })); };
  const isCo = f.owner_type === "Société";
  const [sirenBusy, setSirenBusy] = useState(false);
  const lookupSiren = async () => {
    const sid = String(f.siren || "").replace(/\D/g, "");
    if (sid.length !== 9) { toast.error("Le SIREN doit comporter 9 chiffres"); return; }
    setSirenBusy(true);
    try {
      const { data } = await api.get(`/owners/siren-lookup?siren=${sid}`);
      setF((x) => ({ ...x, siren: data.siren, company: data.company || x.company, vat_number: data.vat_number, address: data.address || x.address, postal_code: data.postal_code || x.postal_code, city: data.city || x.city, country: data.country || x.country }));
      setErrors({});
      toast.success(data.active ? `${data.company} — informations complétées` : `${data.company} — attention : entreprise fermée`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Recherche impossible"); }
    finally { setSirenBusy(false); }
  };
  const req = Object.fromEntries(REQUIRED[f.owner_type].map(([k]) => [k, true]));
  const inp = (k) => `w-full px-3 py-2.5 rounded-xl border text-sm outline-none bg-white focus:border-brand-lime ${errors[k] ? "border-red-400 bg-red-50/40" : "border-brand-soft"}`;
  const field = (k, label, props = {}) => (
    <label className="block text-xs font-medium text-brand-green/70">{label}{req[k] ? " *" : ""}
      <input value={f[k] || ""} onChange={(e) => set(k, e.target.value)} className={`${inp(k)} mt-1`} data-testid={`owner-f-${k}`} {...props} />
      {errors[k] && <span className="text-[11px] text-red-500">Champ obligatoire</span>}
    </label>
  );
  const save = async () => {
    const missing = REQUIRED[f.owner_type].filter(([k]) => !String(f[k] || "").trim());
    if (!missing.length && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) {
      setErrors({ email: true }); toast.error("Adresse e-mail invalide"); return;
    }
    if (missing.length) {
      setErrors(Object.fromEntries(missing.map(([k]) => [k, true])));
      toast.error(`Champs obligatoires manquants : ${missing.map(([, l]) => l).join(", ")}`);
      return;
    }
    setBusy(true);
    try {
      const { data } = f.id ? await api.put(`/owners/${f.id}`, f) : await api.post("/owners", f);
      toast.success("Propriétaire enregistré");
      onSaved(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const g2 = "grid sm:grid-cols-2 gap-3";
  return (
    <div className="space-y-6" data-testid="owner-form">
      <div className="inline-flex p-1 rounded-full bg-brand-softer border border-brand-soft" data-testid="owner-type-switch">
        {[["Société", "Société / Autre", Building2], ["Particulier", "Particulier", User]].map(([v, l, I]) => (
          <button key={v} type="button" onClick={() => set("owner_type", v)} className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold ${f.owner_type === v ? "bg-brand-greenDeep text-white" : "text-brand-greenDeep"}`} data-testid={`owner-type-${v === "Société" ? "societe" : "particulier"}`}><I className="w-4 h-4" /> {l}</button>
        ))}
      </div>
      {isCo ? (
        <Section title="Informations société" hint="Ces informations figureront sur les quittances de loyer et autres documents.">
          {field("company", "Société")}
          <div className={g2}>
            {field("vat_number", "No. TVA")}
            <div className="flex items-end gap-2">
              <div className="flex-1">{field("siren", "SIREN", { placeholder: "9 chiffres", inputMode: "numeric" })}</div>
              <button type="button" onClick={lookupSiren} disabled={sirenBusy} className="mb-[1px] shrink-0 inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep text-xs font-semibold disabled:opacity-60" data-testid="owner-siren-lookup">{sirenBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Remplir</button>
            </div>
          </div>
          <p className="text-[11px] text-brand-green/45">Saisissez le SIREN puis « Remplir » : raison sociale, adresse et n° de TVA sont complétés automatiquement.</p>
        </Section>
      ) : (
        <Section title="Informations personnelles">
          <div className="grid sm:grid-cols-3 gap-3">
            <label className="block text-xs font-medium text-brand-green/70">Civilité *
              <select value={f.civilite || ""} onChange={(e) => set("civilite", e.target.value)} className={`${inp("civilite")} mt-1`} data-testid="owner-f-civilite">
                <option value="">—</option><option>Monsieur</option><option>Madame</option>
              </select>
              {errors.civilite && <span className="text-[11px] text-red-500">Champ obligatoire</span>}
            </label>
            {field("prenom", "Prénom")}{field("nom", "Nom")}
          </div>
          <div className={g2}>{field("date_naissance", "Date de naissance", { type: "date" })}{field("lieu_naissance", "Lieu de naissance")}</div>
        </Section>
      )}
      <Section title="Informations de contact">
        <div className="grid sm:grid-cols-3 gap-3">{field("email", "E-mail", { type: "email" })}{field("mobile", "Mobile")}{field("phone", "Téléphone")}</div>
        {field("address", "Adresse")}
        <div className="grid sm:grid-cols-3 gap-3">{field("city", "Ville")}{field("postal_code", "Code postal")}{field("country", "Pays")}</div>
      </Section>
      {isCo && <AssetField kind="logo" path={f.logo_path} onChange={(p) => set("logo_path", p)} label="Votre logo" hint="Ajoutez un logo pour personnaliser vos documents." />}
      <AssetField kind="signature" path={f.signature_path} onChange={(p) => set("signature_path", p)} label="Signature" hint="Ajoutez votre signature scannée pour personnaliser vos documents, ou dessinez-la." />
      {isCo && (
        <Section title="Pied de page" hint="Laissez vide pour utiliser le pied de page par défaut.">
          <textarea value={f.footer || ""} onChange={(e) => set("footer", e.target.value)} rows={3} className={inp("footer")} data-testid="owner-f-footer" />
        </Section>
      )}
      {!isCo && (
        <Section title="Pièce d'identité">
          <div className="grid sm:grid-cols-3 gap-3">
            <label className="block text-xs font-medium text-brand-green/70">Type
              <select value={f.piece_type || ""} onChange={(e) => set("piece_type", e.target.value)} className={`${inp("piece_type")} mt-1`} data-testid="owner-f-piece_type">
                <option value="">—</option><option>Carte d'identité</option><option>Passeport</option><option>Titre de séjour</option><option>Permis de conduire</option>
              </select>
            </label>
            {field("piece_numero", "Numéro")}{field("piece_expiration", "Expiration", { type: "date" })}
          </div>
        </Section>
      )}
      <Section title="Coordonnées bancaires" hint="Vous pouvez renseigner les coordonnées bancaires du compte.">
        <div className={g2}>{BANK.map(([k, l]) => <React.Fragment key={k}>{field(k, l)}</React.Fragment>)}</div>
      </Section>
      <div className="flex gap-2 sticky -bottom-6 bg-white pt-3 pb-6 -mb-6 border-t border-brand-soft">
        <button type="button" onClick={onCancel} className="flex-1 py-3 rounded-full border border-brand-soft text-brand-green text-sm" data-testid="owner-cancel">Annuler</button>
        <button type="button" onClick={save} disabled={busy} className="flex-1 py-3 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="owner-mgr-save">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Enregistrer</button>
      </div>
    </div>
  );
}

export function OwnersManager({ owners, onClose, reload, reloadProps }) {
  const [editing, setEditing] = useState(null);
  const [formKey, setFormKey] = useState(0);
  useEffect(() => { if (!owners.length) setEditing({}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const remove = async (o) => {
    if (!window.confirm(`Supprimer ${o.name} ?`)) return;
    try { await api.delete(`/owners/${o.id}`); await reload(); if (editing?.id === o.id) setEditing(null); toast.success("Supprimé"); }
    catch { toast.error("Erreur"); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="owners-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-brand-soft">
          <h3 className="font-display text-2xl font-semibold text-brand-greenDeep">Propriétaires</h3>
          <button onClick={onClose} className="text-brand-green/50 hover:text-brand-green" data-testid="owners-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 grid md:grid-cols-[260px_1fr] min-h-0">
          <div className="border-r border-brand-soft p-4 overflow-y-auto space-y-2">
            <button onClick={() => { setEditing({}); setFormKey((k) => k + 1); }} className="w-full py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm flex items-center justify-center gap-2" data-testid="owner-mgr-add"><Plus className="w-4 h-4" /> Nouveau propriétaire</button>
            {owners.map((o) => (
              <div key={o.id} onClick={() => setEditing(o)} className={`group flex items-center gap-2 px-3 py-2.5 rounded-xl cursor-pointer ${editing?.id === o.id ? "bg-brand-lime/25" : "bg-brand-softer hover:bg-brand-soft"}`} data-testid={`owner-row-${o.id}`}>
                {(o.owner_type || "").startsWith("Soci") ? <Building2 className="w-4 h-4 text-brand-green/50" /> : <User className="w-4 h-4 text-brand-green/50" />}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-brand-greenDeep truncate">{o.name}</p>
                  <p className="text-[11px] text-brand-green/50 truncate">{(o.owner_type || "Particulier").startsWith("Soci") ? "Société / Autre" : "Particulier"}{o.city ? ` · ${o.city}` : ""}</p>
                </div>
                <button onClick={(e) => { e.stopPropagation(); remove(o); }} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600" data-testid={`owner-delete-${o.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {owners.length === 0 && <p className="text-xs text-brand-green/50 text-center py-4">Aucun propriétaire enregistré.</p>}
          </div>
          <div className="p-6 overflow-y-auto">
            {editing ? (
              <OwnerForm key={editing.id || `new-${formKey}`} initial={editing} onCancel={() => setEditing(null)} onSaved={async (d) => { await reload(); reloadProps?.(); setEditing(d); }} />
            ) : <p className="text-sm text-brand-green/50 py-10 text-center">Sélectionnez un propriétaire pour modifier sa fiche, ou créez-en un nouveau.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
